import {
  Box,
  CircularProgress,
  InputAdornment,
  List,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useState } from 'react';

import { ApplicationCount } from '@features/applications';
import ApplicationsCountsListItem from '@features/applications/components/ApplicationsCountsListItem/ApplicationsCountsListItem';
import { useGetEnterpriseApplicationsCounts } from '@features/applications/hooks/useGetEnterpriseApplicationsCounts';
import { white } from '@styles/colors';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';

type ApplicationsCountsListProps = {
  selectedPositions: string[];
  selectedCompanyId: string | number;
  setSelectedPositions: Dispatch<SetStateAction<string[]>>;
  disableButtons: boolean;
  handlePageNo: (newPageNo: number) => void;
};

const EnterpriseApplicationsCountsList: FC<ApplicationsCountsListProps> = ({
  selectedPositions,
  selectedCompanyId,
  setSelectedPositions,
  disableButtons,
  handlePageNo,
}) => {
  const isXLarge = useMediaQuery('(min-width:1370px)');
  const [searchValue, setSearchValue] = useState('');
  const { applicationsCountsData, isLoadingApplicationsCounts } =
    useGetEnterpriseApplicationsCounts({
      query: searchValue,
      companyId: selectedCompanyId,
    });

  const handleTogglePosition = (positionTitle: string) => {
    setSelectedPositions((prevSelectedPositions) => {
      if (prevSelectedPositions.includes(positionTitle)) {
        return prevSelectedPositions.filter((title) => title !== positionTitle);
      }

      return [...prevSelectedPositions, positionTitle];
    });
  };

  const getTotalApplicationsCount = () =>
    applicationsCountsData?.reduce(
      (acc, cur) => ({
        ...acc,
        unattended: acc.unattended + cur.unattended,
        messaged: acc.messaged + cur.messaged,
        attended: acc.attended + cur.attended,
        interview_process: acc.interview_process + cur.interview_process,
      }),
      {
        s_title: 'All',
        unattended: 0,
        messaged: 0,
        attended: 0,
        interview_process: 0,
      },
    );

  return (
    <Box bgcolor={white} borderRadius="8px" py={3} mt={2} mb={2}>
      <Stack
        sx={{ flexDirection: isXLarge ? 'row' : 'column' }}
        justifyContent="space-between"
        alignItems={isXLarge ? 'center' : 'start'}
        px={3}
        mb={2}
      >
        <Typography
          variant="h4"
          color="secondary"
          fontSize="24px"
          fontWeight="bold"
          px={3}
        >
          Positions:
        </Typography>
        <TextField
          variant="outlined"
          size="small"
          color="secondary"
          fullWidth
          placeholder="Search Position&hellip;"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="secondary" sx={{ fontSize: '20px' }} />
              </InputAdornment>
            ),
            endAdornment: searchValue ? (
              <InputAdornment
                position="end"
                onClick={() => setSearchValue('')}
                sx={{ cursor: 'pointer' }}
              >
                <ClearIcon color="secondary" sx={{ fontSize: '18px' }} />
              </InputAdornment>
            ) : null,
          }}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          sx={{
            '& input': {
              fontSize: '14px',
            },
            padding: '10px',
          }}
        />
      </Stack>
      {isLoadingApplicationsCounts ? (
        <List sx={{ textAlign: 'center', margin: '10px' }}>
          <CircularProgress />
        </List>
      ) : applicationsCountsData?.length ? (
        <List>
          {applicationsCountsData.length > 1 && (
            <ApplicationsCountsListItem
              key="All"
              applicationCount={getTotalApplicationsCount() as ApplicationCount}
              selected={!selectedPositions.length}
              handleTogglePosition={() => setSelectedPositions([])}
              disabled={disableButtons}
              handlePageNo={handlePageNo}
            />
          )}
          {applicationsCountsData
            .sort((a, b) => a.s_title.localeCompare(b.s_title))
            .map((applicationCount) => (
              <ApplicationsCountsListItem
                key={applicationCount.s_title}
                applicationCount={applicationCount}
                selected={selectedPositions.includes(applicationCount.s_title)}
                handleTogglePosition={() =>
                  handleTogglePosition(applicationCount.s_title)
                }
                disabled={disableButtons}
                handlePageNo={handlePageNo}
              />
            ))}
        </List>
      ) : (
        <Typography sx={{ textAlign: 'center', margin: '10px' }}>
          No Position Found
        </Typography>
      )}
    </Box>
  );
};

export default EnterpriseApplicationsCountsList;
